import { action, observable, flow, makeObservable } from 'mobx';
import { ApiResponse, GlobalStore, GridStore, } from '@roc/feature-app-core';
import { TaxService } from '../services/taxService';
import { PaymentApprovalFilter, TaxTabs } from '../types/types';
import { FileUpload, SelectOption } from '@roc/ui';
import { downloadDocument } from '@roc/feature-utils';

export class TaxCertificateStore {
  globalStore: GlobalStore;
  service: TaxService;
  taxCertificateGridStore: GridStore;
  tab: TaxTabs;
  loanIds: number[] = [];
  paymentAprovalSelectedFilter: PaymentApprovalFilter;
  loan: any;
  detailsModalOpen: boolean;
  selectedRecord: any;
  totalDetailsAmount: number;
  taxPaymentDetailsGridStore: GridStore;
  showSelectedRecords = false;

  constructor(globalStore: GlobalStore) {
    this.globalStore = globalStore;
    this.service = new TaxService();
    this.tab = TaxTabs.TAX_CERTIFICATE;
    this.taxCertificateGridStore = new GridStore(() => this.fetchTaxDashboard(),
      null,
      50);
    this.taxPaymentDetailsGridStore = new GridStore(() => this.fetchPaymentApprovalModalDashboard(),
      null,
      50);
    this.paymentAprovalSelectedFilter = PaymentApprovalFilter.PENDING;
    makeObservable(this, {
      createTaxCertificate: flow,
      tab: observable,
      setTab: action,
      loanIds: observable,
      createRedemptionRequest: flow,
      updatePropertyTaxStatus: flow,
      uploadPaymentZip: flow,
      uploadRefundsFile: flow,
      sendPaymentApproval: flow,
      sendAllPaymentApproval: flow,
      sendTaxIdsPaymentApproval: flow,
      setPaymentAprovalSelectedFilter: action,
      paymentAprovalSelectedFilter: observable,
      loan: observable,
      setLoan: action,
      exportPropertyTaxReport: flow,
      detailsModalOpen: observable,
      selectedRecord: observable,
      totalDetailsAmount: observable,
      openModal: action,
      closeModal: action,
      showSelectedRecords: observable,
      setShowSelectedRecords: action,
      resetToolbarInfo: action,
    });
  };

  private async fetchTaxDashboard() {
    switch (this.tab) {
      case TaxTabs.TAX_CERTIFICATE:
      case TaxTabs.REDEMPTION_REQUEST:
        return await this.fetchTaxCertificateDashboard();
      case TaxTabs.PAYMENT_APPROVAL:
        return await this.fetchPaymentApprovalDashboard();
      default:
        return await this.fetchTaxCertificateDashboard();
    }
  }

  private async fetchTaxCertificateDashboard() {
    try {
      const filters = {
        ...this.taxCertificateGridStore.gridData.meta.filters
      };
      const dropdownFilters = {
        loanId: this.loanIds.length > 0 ? this.loanIds.join(',') : '0',
        ...this.taxCertificateGridStore.gridData.meta.dropdownFilters
      };
      const response: ApiResponse = await this.service.getTaxCertificateDashboard(
        this.tab,
        this.taxCertificateGridStore.gridData.meta.pageNumber,
        this.taxCertificateGridStore.gridData.meta.pageSize,
        this.taxCertificateGridStore.gridData.meta.sortDir,
        this.taxCertificateGridStore.gridData.meta.sortBy,
        filters,
        this.showSelectedRecords ? dropdownFilters : this.taxCertificateGridStore.gridData.meta.dropdownFilters
      );
      return response;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while fetching tax dashboard.',
      });
    }
  };


  private async fetchPaymentApprovalDashboard() {
    try {
      const filters = {
        ...this.taxCertificateGridStore.gridData.meta.filters
      };
      const response: ApiResponse = await this.service.getTaxPaymentDashboard(
        this.paymentAprovalSelectedFilter,
        null,
        this.taxCertificateGridStore.gridData.meta.pageNumber,
        this.taxCertificateGridStore.gridData.meta.pageSize,
        this.taxCertificateGridStore.gridData.meta.sortDir,
        this.taxCertificateGridStore.gridData.meta.sortBy,
        filters,
        this.taxCertificateGridStore.gridData.meta.dropdownFilters
      );
      return response;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while fetching tax payment dashboard.',
      });
    }
  };

  private async fetchPaymentApprovalModalDashboard() {
    try {
      const filters = {
        ...this.taxPaymentDetailsGridStore.gridData.meta.filters
      };
      const response: ApiResponse = await this.service.getTaxPaymentDashboard(
        PaymentApprovalFilter.TAPE,
        this.selectedRecord.tape,
        this.taxPaymentDetailsGridStore.gridData.meta.pageNumber,
        this.taxPaymentDetailsGridStore.gridData.meta.pageSize,
        this.taxPaymentDetailsGridStore.gridData.meta.sortDir,
        this.taxPaymentDetailsGridStore.gridData.meta.sortBy,
        filters,
        this.taxPaymentDetailsGridStore.gridData.meta.dropdownFilters
      );
      this.totalDetailsAmount = parseFloat(response.data.data.rows.reduce((total, payable) => total + payable.amount, 0).toFixed(2));
      return response;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while fetching tax payment dashboard.',
      });
    }
  };

  *createTaxCertificate() {
    try {
      const response: ApiResponse = yield this.service.createTaxCertificate(this.loan?.loanId);
      if (response.data.status === 'OK') {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Order created successfully.'
        });
        this.taxCertificateGridStore.resetAndFetchGridData();
      } else {
        this.globalStore.notificationStore.showErrorNotification({
          message: response.data?.error?.message
        });
      }
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while creating tax certificate.',
      });
    }
  };

  *createRedemptionRequest() {
    try {
      const response: ApiResponse = yield this.service.createRedemptionRequest(this.loanIds);
      if (response.data.status === 'OK') {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Redemption request created successfully.'
        });
        this.taxCertificateGridStore.resetAndFetchGridData();
        this.clearLoanIds();
      } else {
        this.globalStore.notificationStore.showErrorNotification({
          message: response.data?.error?.message
        });
      }
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while creating redemption request.',
      });
    }
  };

  setTab(tab: TaxTabs) {
    this.tab = tab;
  };

  setLoanIds = (id, selected) => {
    if (id != null && !selected) {
      this.removeLoanIds(id);
    } else if (id != null && !this.loanIds.includes(id)) {
      this.loanIds.push(id);
    }
  }

  clearLoanIds = () => {
    this.loanIds = [];
  }

  removeLoanIds(id) {
    this.loanIds = this.loanIds.filter(recordsId => recordsId != id);
  }

  *updatePropertyTaxStatus(id: number, status: string) {
    const proccesed = false;
    try {
      const response = yield this.service.updatePropertyTaxStatus(id, status);
      return response.data.success;
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while updating property tax status.',
      });
    }
  }

  *uploadPaymentZip(
    fileUpload: FileUpload,
  ) {
    try {
      var newFile;
      if (fileUpload) {
        const file = fileUpload.file;
        const blob = file.slice(0, file.size, file.type);
        newFile = new File([blob], fileUpload.name, { type: file.type });
      }

      yield this.service.uploadPaymentZip(
        newFile
      );
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Payment zip file uploaded successfully.',
      });
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while uploading payment zip file.',
      });
    }
  };

  *uploadRefundsFile(
    fileUpload: FileUpload,
  ) {
    try {
      var newFile;
      if (fileUpload) {
        const file = fileUpload.file;
        const blob = file.slice(0, file.size, file.type);
        newFile = new File([blob], fileUpload.name, { type: file.type });
      }

      yield this.service.uploadRefundsFile(
        newFile
      );
      this.globalStore.notificationStore.showSuccessNotification({
        message: 'Refunds file uploaded successfully.',
      });
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while uploading refunds file.',
      });
    }
  };

  *sendPaymentApproval(taxIds) {
    try {
      const response: ApiResponse = yield this.service.sendPaymentApproval(taxIds);
      if (response.data.status === 'OK') {
        this.globalStore.notificationStore.showSuccessNotification({
          message: 'Payment approval sent correctly.'
        });
        this.taxCertificateGridStore.resetAndFetchGridData();
      } else {
        this.globalStore.notificationStore.showErrorNotification({
          message: response.data?.error?.message
        });
      }
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while sending payment approval.',
      });
    }
  };

  *sendAllPaymentApproval() {
    yield this.sendPaymentApproval([])
  };

  *sendTaxIdsPaymentApproval() {
    const taxIds = this.taxPaymentDetailsGridStore.gridData.data.rows.map(r => r.id);
    yield this.sendPaymentApproval(taxIds);
  };

  setPaymentAprovalSelectedFilter(filter: PaymentApprovalFilter) {
    this.paymentAprovalSelectedFilter = filter;
    this.taxCertificateGridStore.resetAndFetchGridData();
  }

  setLoan(value) {
    this.loan = value;
  }

  *exportPropertyTaxReport() {
    try {
      const filters = {
        ...this.taxCertificateGridStore.gridData.meta.filters
      };
      const response: ApiResponse = yield this.service.exportPropertyTaxReport(
        this.paymentAprovalSelectedFilter,
        filters,
        this.taxCertificateGridStore.gridData.meta.dropdownFilters
      );
      downloadDocument(response.data, response.headers, 'download', 'TAX_REPORT.xlsx');
    } catch (error) {
      this.globalStore.notificationStore.showErrorNotification({
        message: 'Error while exporting special servicing dashboard data.',
      });
    }
  };

  closeModal() {
    this.detailsModalOpen = false;
  }

  openModal(taxRecord: any) {
    this.detailsModalOpen = true;
    this.selectedRecord = taxRecord;
  }

  setShowSelectedRecords(value: boolean) {
    this.showSelectedRecords = value;
  }

  resetToolbarInfo() {
    this.clearLoanIds();
  }

}
